import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { useQuery } from '@tanstack/react-query'
import { documentCurrenciesQuery } from '@/components/document-currency-select'
import { SUPPORTED_CURRENCIES } from '@/lib/currencies'
import { meQuery } from '@/lib/me'

/**
 * The currencies a document or supplier may be in: the company's own and
 * every currency it holds a rate for (foreign-currency suppliers). The list
 * is the server's; `value` and `baseCurrency` are always offered so a saved
 * record still reads while the list loads.
 */
export function companyCurrencyCodes(
  listed: Array<{ code: string }> | undefined,
  baseCurrency: string | undefined,
  value: string,
): string[] {
  return [
    ...new Set([baseCurrency ?? '', ...(listed ?? []).map((option) => option.code), value]),
  ].filter((code) => code !== '')
}

export function useCompanyCurrencies() {
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null
  const baseCurrency = me?.memberships.find(
    (membership) => membership.company_id === companyId,
  )?.company_base_currency
  const currencies = useQuery(documentCurrenciesQuery(companyId))
  return { baseCurrency, currencies }
}

/**
 * The currencies a record may be raised in — the company's own and every
 * currency it holds a rate for, plus `value` so a saved record still reads.
 * Rates live in Accounting; a company without it is offered the supported
 * currencies instead, and nothing is asked of a module it cannot reach.
 */
export function useCompanyCurrencyOptions(value: string): string[] {
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null
  const accounting = Boolean(me?.enabled_modules?.includes('accounting'))
  const baseCurrency = me?.memberships.find(
    (membership) => membership.company_id === companyId,
  )?.company_base_currency
  const currencies = useQuery({
    ...documentCurrenciesQuery(companyId),
    enabled: Boolean(companyId) && accounting,
  })

  if (!accounting || currencies.isError) {
    return [...new Set<string>([...SUPPORTED_CURRENCIES, value])].filter((code) => code !== '')
  }
  return companyCurrencyCodes(currencies.data, baseCurrency, value)
}

interface CompanyCurrencySelectProps {
  id?: string
  value: string
  onValueChange: (currency: string) => void
  testId?: string
  className?: string
}

export function CompanyCurrencySelect({
  id,
  value,
  onValueChange,
  testId,
  className,
}: CompanyCurrencySelectProps) {
  const { baseCurrency, currencies } = useCompanyCurrencies()
  const codes = companyCurrencyCodes(currencies.data, baseCurrency, value)

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id={id} className={className} data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {codes.map((code) => (
          <SelectItem key={code} value={code}>
            <span className="font-mono">{code}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
